import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
    ChangeDetectionStrategy
} from '@angular/core';
import { IdGeneratorPipe } from '../../pipes/id-generator.pipe';
import { debounce } from '../../utils/debounce';
import { IGenericGridPaginationConfig } from '../generic-grid/generic-grid.component';

enum ColumnDataType {
    String = 1,
    Bool,
    Date,
    Number,
    Percentage,
    Amount
}

interface IRowDataCellContent {
    value?: any;
}

@Component({
    selector: 'lib-dynamic-grid',
    templateUrl: './dynamic-grid.component.html',
    styleUrls: ['./dynamic-grid.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [IdGeneratorPipe]
})
export class DynamicGridComponent implements OnChanges {
    @Input()
    source?: any;

    @Input()
    paginationConfig?: IGenericGridPaginationConfig;

    @Input()
    dynamicGridId?: string;

    @Output()
    paginationConfigChange: EventEmitter<IGenericGridPaginationConfig>;

    rowsContent: any;

    columnsHeader: any;

    rowGridContent: IRowDataCellContent[][];

    rowData: any;

    @ViewChild('scrollable') scrollableRef!: ElementRef;

    debounceScroll: () => void;

    constructor() {
        this.paginationConfigChange = new EventEmitter<IGenericGridPaginationConfig>();
        this.columnsHeader = [];
        this.rowsContent = [];
        this.rowGridContent = [];
        this.debounceScroll = debounce(this.onScroll.bind(this), 200);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['source']?.currentValue) {
            const data = changes['source']?.currentValue;

            this.columnsHeader = data.columns
                ? data.columns.map((c: { reportColumnTitle: string }) => ({
                      title: c.reportColumnTitle
                  }))
                : [];

            this.rowsContent = [];
            this.rowsContent = data.rows ? this.rowsContent.concat(data.rows ?? []) : [];

            if (this.rowsContent.length) this.buildRowGridContent();
        }
    }

    canGoNext() {
        if (!this.paginationConfig?.currentPage || !this.paginationConfig?.pagesCount) return false;
        return this.paginationConfig?.currentPage < this.paginationConfig?.pagesCount;
    }

    goNext() {
        if (!this.canGoNext()) return;
        const nextPage = this.paginationConfig ? this.paginationConfig.currentPage + 1 : 1;
        this.changePage(nextPage);
    }

    changePage(page: number) {
        if (!this.paginationConfig) return;
        this.paginationConfigChange.emit({ ...this.paginationConfig, currentPage: page });
    }

    onScroll() {
        if (
            Math.round(
                (this.scrollableRef.nativeElement as HTMLElement).scrollTop +
                    (this.scrollableRef.nativeElement as HTMLElement).clientHeight
            ) >=
            (this.scrollableRef.nativeElement as HTMLElement).scrollHeight - 10
        ) {
            this.goNext();
        }
    }

    private transformCellData(cell: any): IRowDataCellContent {
        switch (cell['reportColumnDataType']) {
            case ColumnDataType.String:
            case ColumnDataType.Number:
                return { value: cell.reportCellValue };

            case ColumnDataType.Date:
                return {
                    value: cell.reportCellValue ? new Date(cell.reportCellValue).toLocaleDateString('en-US') : ''
                };

            case ColumnDataType.Percentage:
                return { value: cell.reportCellValue ? `${cell.reportCellValue * 100}%` : '' };

            case ColumnDataType.Amount:
                return { value: cell.reportCellValue ? `$${cell.reportCellValue}` : '' };

            default:
                return { value: cell.reportCellValue ? cell.reportCellValue : '' };
        }
    }

    private buildRowGridContent() {
        for (const row of this.rowsContent) {
            this.rowData = [];

            for (const cell of row) {
                const cellValue = this.transformCellData(cell);
                this.rowData.push(cellValue);
            }

            this.rowGridContent.push(this.rowData);
        }
    }
}
